import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ServiceFAQ } from '@/components/sections/ServiceFAQ';
import { useJsonLd } from '@/hooks/useJsonLd';

const taxFAQs = [
  { q: "How much does tax preparation cost with a CPA in Charlotte, NC?", a: "Tax preparation fees vary based on complexity. Individual returns (Form 1040) with straightforward W-2 income start at a different price point than returns involving rental properties, investments, or multi-state filing. KGOB provides transparent pricing after reviewing your specific situation. We serve clients at our Charlotte, Winston-Salem, and Mooresville offices, as well as remotely across the United States." },
  { q: "What's the difference between tax preparation and tax planning?", a: "Tax preparation is filing your return after the year ends — reporting what already happened. Tax planning is proactive strategy throughout the year to minimize what you'll owe before filing season. KGOB specializes in both, but the biggest savings come from year-round planning: entity structuring, retirement optimization, timing of income and deductions, and quarterly estimate management." },
  { q: "Should I file married filing jointly or separately?", a: "In most cases, married filing jointly results in a lower tax bill due to wider tax brackets, higher standard deductions, and access to more credits. However, there are specific situations — student loan repayment, medical expense thresholds, or liability concerns — where filing separately saves money. A KGOB CPA can run both scenarios with your actual numbers to determine which filing status saves you the most." },
  { q: "Can KGOB help me if I'm not in North Carolina?", a: "Absolutely. While KGOB is headquartered in Charlotte, NC with offices in Winston-Salem and Mooresville, we serve clients across all 50 states. Tax preparation, planning, and IRS representation are handled securely through our encrypted client portal — no in-person visit required." },
  { q: "What tax deductions do small business owners commonly miss?", a: "The most frequently missed deductions include home office expenses, vehicle mileage, health insurance premiums for self-employed individuals, retirement plan contributions (SEP-IRA, Solo 401k), Section 199A qualified business income deduction, and business meals. Many business owners also miss the opportunity to restructure from a sole proprietorship to an S-Corp, which can save $5,000-$20,000+ annually in self-employment tax." },
  { q: "When should I switch from doing my own taxes to hiring a CPA?", a: "Consider a CPA when your tax situation involves any of these: income over $100K, self-employment or side business income, rental properties, investment portfolios, stock options or RSUs, major life changes (marriage, divorce, home purchase), or multi-state income. The cost of professional preparation is almost always recovered through deductions and strategies that tax software doesn't catch." }
];

const TaxServices = () => {
    useJsonLd('tax-0', {"@context": "https://schema.org", "@type": "Service", "name": "Tax Planning & Tax Advisor Services", "description": "Individual and business tax preparation with proactive year-round tax planning. All filing statuses, multi-state, investments, rental properties, and complex situations. Licensed CPAs in Charlotte, NC serving clients nationwide.", "url": "https://kgob.com/services/tax", "provider": {"@type": "AccountingService", "name": "KGOB CPAs & Advisors", "url": "https://kgob.com", "address": {"@type": "PostalAddress", "addressLocality": "Charlotte", "addressRegion": "NC", "addressCountry": "US"}}, "serviceType": ["Tax Preparation", "Tax Planning", "Individual Tax Returns", "Business Tax Returns", "Multi-State Tax Filing", "Quarterly Estimated Taxes"], "areaServed": {"@type": "Country", "name": "United States"}});

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Tax Advisor & Tax Planning Services | Individuals & Businesses | KGOB</title>
        <meta name="description" content="Work with KGOB tax advisors for individual tax preparation and year-round tax planning. Compliance handled, strategy built in for owners and families." />
        <link rel="canonical" href="https://kgob.com/services/tax" />
        <meta property="og:title" content="Tax Advisor & Tax Planning Services | Individuals & Businesses | KGOB" />
        <meta property="og:description" content="Work with KGOB tax advisors for individual tax preparation and year-round tax planning. Compliance handled, strategy built in for owners and families." />
        <meta property="og:url" content="https://kgob.com/services/tax" />
        <meta property="og:type" content="website" />
      </Helmet>
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240] py-20 lg:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h1 className="font-['Playfair_Display'] text-5xl font-bold text-[#F7F6F2] mb-6">
                Tax Planning & Tax Advisor Services
              </h1>
              
              <p className="text-xl text-[#D4AF37] italic mb-4">
                Compliance handled. Strategy built in. Year-round planning for individuals and businesses.
              </p>
              
              <p className="text-lg text-[#F7F6F2]/90 mb-6">
                KGOB tax advisors provide year-round tax planning services and individual tax preparation for owners and families who want more than a once-a-year filing. We integrate tax work with your books and overall strategy.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Button data-testid="tax-services-button-4" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                  <Link data-testid="tax-services-link-4" to="/book-consultation?source=services-tax">Request Tax Consultation</Link>
                </Button>
                <Button data-testid="tax-services-button-3" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                  <Link data-testid="tax-services-link-3" to="/services">View All Services</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Keyword focus */}
        <section className="py-16 bg-white" data-testid="tax-keyword-section">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
            <h2 className="font-['Playfair_Display'] text-3xl font-semibold text-[#0B2240] mb-4">
              Tax planning and individual tax preparation
            </h2>
            <p className="text-lg text-[#4C5A70] max-w-3xl mx-auto">
              One team acts as your tax advisor for both the filing and the forward plan, so each return reflects decisions made months earlier. Serving clients from our North Carolina offices and nationwide.
            </p>
          </div>
        </section>

        {/* Is This Right for You? */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              Is This Right for You?
            </h2>
            
            <Card className="p-8 border-2 border-[#E1DFD7]">
              <p className="text-[#4C5A70] mb-6">
                Our tax services are designed for individuals and business owners who want proactive, year-round tax planning integrated with compliance work. You will benefit most if:
              </p>
              
              <div className="space-y-3">
                {[
                  'You want a CPA who thinks beyond April 15',
                  'You need multi-state or multi-entity tax returns prepared',
                  'You are looking for proactive tax planning to reduce your liability',
                  'You want your tax work coordinated with your bookkeeping and financial planning',
                  'You have complex situations like rental properties, investments, or business ownership'
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-[#4C5A70]">{item}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* What You Get */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What You Get
            </h2>
            
            <div className="space-y-8">
              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Individual Tax Preparation</h3>
                <p className="text-[#4C5A70] mb-4">
                  Federal and state personal income tax returns for individuals, families, and high-net-worth clients.
                </p>
                <ul className="space-y-2">
                  {[
                    'Form 1040 with all schedules (A, B, C, D, E, etc.)',
                    'Multi-state returns where you live or work across states',
                    'Rental property income and depreciation tracking',
                    'Investment income, capital gains, and stock option reporting'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Business Tax Preparation</h3>
                <p className="text-[#4C5A70] mb-4">
                  Full-service business tax preparation for S corporations, partnerships, LLCs, and C corporations.
                </p>
                <ul className="space-y-2">
                  {[
                    'Form 1120S (S corporation), 1065 (partnership), 1120 (C corp)',
                    'Multi-state business returns and apportionment',
                    'Quarterly estimated tax planning and payment guidance',
                    'Coordination with your monthly bookkeeping'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Proactive Tax Planning</h3>
                <p className="text-[#4C5A70] mb-4">
                  Year-round strategy conversations to reduce your tax liability and prepare for major financial decisions.
                </p>
                <ul className="space-y-2">
                  {[
                    'Mid-year and year-end tax projections',
                    'Estimated tax payment schedules',
                    'Entity structure and election timing (S corp, etc.)',
                    'Retirement contribution and HSA planning'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              How It Works
            </h2>
            
            <div className="space-y-6">
              {[
                { step: '1', title: 'Initial Tax Consultation', desc: 'We review your tax situation, prior returns, and identify planning opportunities.' },
                { step: '2', title: 'Document Gathering', desc: 'We provide a checklist of documents needed and work with you to gather all necessary information.' },
                { step: '3', title: 'Preparation & Review', desc: 'We prepare your returns, review them with you, and answer all questions before filing.' },
                { step: '4', title: 'Filing & Payment', desc: 'We e-file your returns and provide guidance on payment options if taxes are owed.' },
                { step: '5', title: 'Year-Round Planning', desc: 'We stay in touch throughout the year for estimated payments, mid-year projections, and strategy updates.' }
              ].map((item, i) => (
                <Card key={i} className="p-6 border-2 border-[#E1DFD7]">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                      <span className="text-[#0B2240] font-bold text-lg">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-2">{item.title}</h3>
                      <p className="text-[#4C5A70]">{item.desc}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Changes */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What Changes
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Functional</h3>
                <p className="text-[#4C5A70] text-sm">
                  Your tax returns are accurate, filed on time, and coordinated with your bookkeeping and financial planning.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Emotional</h3>
                <p className="text-[#4C5A70] text-sm">
                  You feel confident knowing a CPA is managing your tax compliance and looking for ways to reduce your liability.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Strategic</h3>
                <p className="text-[#4C5A70] text-sm">
                  With proactive planning, you can make smarter decisions about timing income, expenses, and major financial moves.
                </p>
              </Card>
            </div>
          </div>
        </section>

        <ServiceFAQ faqs={taxFAQs} />

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-b from-[#0B2240] to-[#0f2d4f]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-4xl font-bold text-[#F7F6F2] mb-6">
              Ready to Take Control of Your Taxes?
            </h2>
            <p className="text-lg text-[#F7F6F2]/90 mb-10">
              Schedule a consultation to discuss your tax situation. We will review your prior returns and identify planning opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button data-testid="tax-services-button-2" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                <Link data-testid="tax-services-link-2" to="/book-consultation?source=services-tax">Request Tax Consultation</Link>
              </Button>
              <Button data-testid="tax-services-button-1" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                <Link data-testid="tax-services-link-1" to="/services">View All Services</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default TaxServices;